import { useState } from "react";
import { Section, Eyebrow, H2, Lead, GlassCard, Reveal } from "../components/site/Primitives";
import StatusBadge from "../components/site/StatusBadge";
import { Download, FileText, Lock, ArrowRight, CheckCircle2, Circle } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const PHASE1 = [
  { id: "m1", label: "Site Identification", done: false },
  { id: "m2", label: "Environmental Survey (Planned)", done: false },
  { id: "m3", label: "Rezoning Petitions (Planned)", done: false },
  { id: "m4", label: "Remediation Contracts (Planned)", done: false },
  { id: "m5", label: "Phase 1 Groundbreaking (Targeted)", done: false },
  { id: "m6", label: "First Building Certificate of Occupancy (Targeted)", done: false },
];

const ROADMAP = [
  { phase: "Phase 1", years: "2026–2027", focus: "Land acquisition · Rezoning · Remediation · Workforce townhomes" },
  { phase: "Phase 2", years: "2027–2029", focus: "Mixed-use riverfront · 220 Event Center flagship · Veteran Housing Building A" },
  { phase: "Phase 3", years: "2029–2031", focus: "Education campus · Light industrial park · Phase 2 of Veteran Housing" },
  { phase: "Phase 4+", years: "2031+", focus: "Regional expansion · Public-private joint ventures · Asset stabilization" },
];

export default function Investors() {
  return (
    <div>
      <Section className="pt-40">
        <div className="flex items-center gap-3 flex-wrap mb-3">
          <Eyebrow>Investor Relations</Eyebrow>
          <StatusBadge kind="in-planning" label="Phase 1 · In Planning" />
        </div>
        <H2 className="max-w-4xl">A disciplined platform. A scalable thesis. A measurable mission.</H2>
        <Lead className="mt-6">
          Phase 1 of the 220 Inc. master plan is in active planning — focused on strategic land
          acquisition, rezoning, environmental remediation, and infrastructure. No assets have
          been acquired and no facilities are operating at this stage. Confidential parcel-level
          details and capital terms are shared only with vetted partners under NDA.
        </Lead>
        <div className="mt-6 text-sm text-white/55">
          Investor Relations · Travis R. McGruder · Founder &amp; CEO · <span className="text-white/80">Travis.McGruder@220inc.com</span>
        </div>
      </Section>

      {/* Snapshot */}
      <Section className="pt-0">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10">
          {[
            { l: "Capital Target", v: "$28.5M" },
            { l: "Capital Stage", v: "Soft-Circling" },
            { l: "Development Status", v: "In Planning" },
            { l: "5-Year ROI (Projected)", v: "18–24%" },
            { l: "10-Year ROI (Projected)", v: "32–48%" },
          ].map((s) => (
            <div key={s.l} className="bg-[#0c0c0e] p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{s.l}</div>
              <div className="font-display text-2xl text-gold">{s.v}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 text-[11px] text-white/45 max-w-3xl leading-relaxed">
          Forward-looking figures. Capital is not closed. Projections reflect modeled returns based on the master plan
          and comparable corridor benchmarks — not realized performance.
        </div>
      </Section>

      {/* Phase 1 milestones */}
      <Section className="border-t border-white/5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow>Phase 1 Milestones</Eyebrow>
            <H2>Where we are. Where we're heading.</H2>
            <Lead className="mt-6">
              Each milestone gates the next deployment of capital. We disclose progress to investors
              in real time via the Investor Portal — never publicly at the parcel level.
            </Lead>
          </div>
          <div className="lg:col-span-7">
            <ol className="space-y-4">
              {PHASE1.map((m, i) => (
                <Reveal key={m.id} delay={i * 0.05}>
                  <div className={`flex items-center gap-5 p-5 border ${m.done ? "border-gold/40 bg-gold/[0.03]" : "border-white/10"}`}>
                    <div className={m.done ? "text-gold" : "text-white/30"}>
                      {m.done ? <CheckCircle2 /> : <Circle />}
                    </div>
                    <div className="flex-1">
                      <div className="font-display text-lg">{m.label}</div>
                      <div className="text-xs uppercase tracking-[0.2em] text-white/40 mt-1">
                        {m.done ? "Complete" : "In progress"}
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </Section>

      {/* Roadmap */}
      <Section className="border-t border-white/5">
        <Eyebrow>Development Roadmap</Eyebrow>
        <H2 className="max-w-3xl">A 10-year, multi-phase plan.</H2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {ROADMAP.map((p) => (
            <GlassCard key={p.phase} className="p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-gold mb-2">{p.years}</div>
              <div className="font-display text-2xl mb-3">{p.phase}</div>
              <p className="text-sm text-white/65 leading-relaxed">{p.focus}</p>
            </GlassCard>
          ))}
        </div>
      </Section>

      {/* Documents */}
      <Section className="border-t border-white/5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow>Investor Documents</Eyebrow>
            <H2>Materials available on request.</H2>
            <Lead className="mt-6">
              Detailed financial models, parcel-level acquisition data, and confidential supplements
              are released only after NDA and accreditation review.
            </Lead>
          </div>
          <div className="lg:col-span-7 space-y-3">
            {[
              { name: "220 Inc. — Investor Overview Packet", size: "3.4 MB", locked: false },
              { name: "Phase 1 Financial Model (Summary)", size: "1.2 MB", locked: false },
              { name: "Veteran Housing Initiative Brief", size: "2.1 MB", locked: false },
              { name: "Confidential Parcel Schedule", size: "—", locked: true },
              { name: "Detailed 10-Year Pro Forma", size: "—", locked: true },
            ].map((d) => (
              <div key={d.name} className="flex items-center justify-between border border-white/10 p-5 hover:border-gold transition-colors">
                <div className="flex items-center gap-4">
                  {d.locked ? <Lock className="text-white/40" size={18} /> : <FileText className="text-gold" size={18} />}
                  <div>
                    <div className="text-sm">{d.name}</div>
                    <div className="text-xs text-white/40">{d.locked ? "Requires NDA · Accreditation" : `PDF · ${d.size}`}</div>
                  </div>
                </div>
                <button
                  data-testid={`doc-${d.name.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => d.locked ? toast.info("Submit an inquiry to access protected documents.") : toast.success(`${d.name} download started.`)}
                  className={d.locked ? "btn-ghost text-xs" : "btn-primary text-xs"}
                >
                  {d.locked ? "Request Access" : <>Download <Download size={14} /></>}
                </button>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section className="border-t border-white/5">
        <InvestorInquiry />
      </Section>
    </div>
  );
}

function InvestorInquiry() {
  const [f, setF] = useState({
    full_name: "", email: "", phone: "", company: "", accreditation: "exploring",
    ticket_size: "100k-500k", interests: [], message: "",
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggleInterest = (i) =>
    setF((x) => ({ ...x, interests: x.interests.includes(i) ? x.interests.filter((s) => s !== i) : [...x.interests, i] }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/investors/inquire", f); toast.success("Inquiry received. Our IR team will reach out within 2 business days."); }
    catch { toast.error("Could not submit. Please try again."); }
  };
  const INTERESTS = ["Phase 1 Land Acquisition", "Veteran Housing", "Mixed-Use Riverfront", "220 Event Center", "Education Campus", "Industrial Park"];

  return (
    <GlassCard className="p-8 md:p-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <Eyebrow>Become a Partner</Eyebrow>
          <H2>Request an investor consultation.</H2>
          <Lead className="mt-4">Tell us a bit about you. We respond personally within 2 business days.</Lead>
        </div>
        <form onSubmit={submit} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="investor-inquiry-form">
          <Input label="Full Name *" required value={f.full_name} onChange={(e) => set("full_name", e.target.value)} />
          <Input label="Email *" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} />
          <Input label="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
          <Input label="Company / Family Office" value={f.company} onChange={(e) => set("company", e.target.value)} />
          <Sel label="Accreditation" value={f.accreditation} onChange={(e) => set("accreditation", e.target.value)}
              options={[{ v: "accredited", l: "Accredited Individual" }, { v: "institutional", l: "Institutional" }, { v: "exploring", l: "Exploring" }]} />
          <Sel label="Ticket Size" value={f.ticket_size} onChange={(e) => set("ticket_size", e.target.value)}
              options={[{ v: "25k-100k", l: "$25K–$100K" }, { v: "100k-500k", l: "$100K–$500K" }, { v: "500k-2m", l: "$500K–$2M" }, { v: "2m+", l: "$2M+" }]} />
          <div className="sm:col-span-2">
            <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-3">Areas of Interest</span>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((i) => (
                <button key={i} type="button" onClick={() => toggleInterest(i)}
                  className={`px-3 py-2 text-xs border transition-colors ${f.interests.includes(i) ? "border-gold text-gold bg-gold/5" : "border-white/10 text-white/70 hover:border-white/30"}`}>
                  {i}
                </button>
              ))}
            </div>
          </div>
          <label className="block sm:col-span-2">
            <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Message</span>
            <textarea rows={4} className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold"
              value={f.message} onChange={(e) => set("message", e.target.value)} />
          </label>
          <div className="sm:col-span-2"><button type="submit" className="btn-primary" data-testid="investor-submit">Submit Inquiry <ArrowRight size={14} /></button></div>
        </form>
      </div>
    </GlassCard>
  );
}

function Input({ label, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">{label}</span>
      <input className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold" {...props} />
    </label>
  );
}
function Sel({ label, options, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">{label}</span>
      <select className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold" {...props}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
